import React from "react"
import _ from "underscore"
import CustomBarChart from "../charts/OldBarChart"
import BarChart from "../charts/OldChart"
import { graphLabelObject } from "../../utilities"

// Matches ImplementationRubric/Report.jsx's own cutoff: rubrics created on or after this date
// always get the monthly trend chart, regardless of whether detailed_result/rubric_update_history
// happen to be present.
const RELEASE_DATE = "2024-08-01T00:00:00.000Z"

const formatMonthYearForLinear = (dateStr) => {
  const [year, month] = dateStr.split("-").slice(0, 2)
  return new Date(year, month - 1, 1).toLocaleString("en-us", { month: "long" })
}

const processRubricData = (rubricData, monthLabels) => {
  if (!rubricData || !rubricData.detailed_result || rubricData.detailed_result.length === 0 || !monthLabels) {
    return []
  }

  const resultsByMonth = rubricData.detailed_result.reduce((acc, result) => {
    const month = formatMonthYearForLinear(result.updated_at)
    return {
      ...acc,
      [month]: acc[month] ? [...acc[month], result] : [result],
    }
  }, {})

  const dataPoints = monthLabels.map((month) => {
    const results = resultsByMonth[month]
    if (results && results.length > 0) {
      const latestResult = results.reduce(
        (latest, result) => (new Date(result.updated_at) > new Date(latest.updated_at) ? result : latest),
        results[0],
      )
      return latestResult.results.Overall || 0
    }
    return 0
  })

  return [{
    label: "Overall", data: dataPoints, backgroundColor: "#CBCACB", borderWidth: 1, spanGaps: false, fill: false,
  }]
}

const buildMonthlyTrendDatasets = (rubric) => {
  const dataSetsLinerGraph = processRubricData(rubric, graphLabelObject)
  const validResults = (rubric.detailed_result || []).filter(
    (result) => result.results && Object.keys(result.results).length > 0,
  )

  return dataSetsLinerGraph.map((dataset, index) => {
    const backgroundColor = Array(graphLabelObject.length).fill("#CBCACB")

    if (validResults.length === 0) {
      return { ...dataset, backgroundColor, data: [] }
    }

    const mostRecentUpdate = validResults.reduce(
      (latest, result) => (new Date(result.updated_at) > new Date(latest.updated_at) ? result : latest),
      _.first(validResults),
    )
    const mostRecentMonthIndex = graphLabelObject.findIndex(
      (month) => month === formatMonthYearForLinear(mostRecentUpdate.updated_at),
    )

    if (index === 0 && mostRecentMonthIndex !== -1) {
      backgroundColor[mostRecentMonthIndex] = "#a6192e"
    }

    const monthResultsMap = validResults.reduce((acc, result) => {
      const monthYear = formatMonthYearForLinear(result.updated_at)
      acc[monthYear] = result.results
      return acc
    }, {})

    const stageDataArray = []
    if (Object.keys(monthResultsMap).length > 0) {
      Object.keys(monthResultsMap[Object.keys(monthResultsMap)[0]]).forEach((stageName) => {
        const stageData = graphLabelObject.map((month) => ({
          month,
          [stageName]: monthResultsMap[month] ? monthResultsMap[month][stageName] : null,
        }))
        stageDataArray.push({ stageName, data: stageData })
      })
    }

    return { ...dataset, backgroundColor, data: stageDataArray }
  })
}

// Renders the exact same chart ImplementationRubric/Report.jsx shows on the Implementation
// section itself: the monthly-history CustomBarChart for any rubric created since the
// 2024-08-01 release (effectively always, today), falling back to a plain per-stage BarChart
// of the current submission only for older rubrics with no monthly history at all.
const ImplementationTrendChart = ({ rubric, onBarChartClick, fixedSize = null }) => {
  const usesMonthlyTrend = (
    rubric.detailed_result && rubric.detailed_result.length > 0 && rubric.rubric_update_history && rubric.rubric_update_history.length > 0
  ) || RELEASE_DATE <= rubric.created_at

  if (usesMonthlyTrend) {
    return (
      <CustomBarChart
        labels={graphLabelObject}
        datasets={buildMonthlyTrendDatasets(rubric)}
        max={100}
        mb={5}
        onClick={onBarChartClick}
        fixedSize={fixedSize}
      />
    )
  }

  // Same per-stage palette ImplementationRubric/Report.jsx's fallback chart uses for the selected
  // rubric, so older rubrics look identical here and on the Implementation section itself.
  const stageColors = ["#A6192E", "#33475C", "#E8A923", "#0CA6A6", "#7D7B7C"]
  const labels = _.map(rubric.report, (stage) => stage.label)
  const datasets = [{
    label: "Current",
    data: rubric.report.map((stage) => stage.completion),
    backgroundColor: stageColors,
    borderColor: stageColors,
    borderWidth: 1,
  }]

  return (
    <BarChart
      labels={labels}
      datasets={datasets}
      max={100}
      mb={fixedSize ? "0" : 5}
      formatPercentage
      fixedSize={fixedSize}
    />
  )
}

export default ImplementationTrendChart
